import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Play, ChevronRight, X, Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/api';
import { resolveMediaUrl, formatDate } from '@/utils/helpers';
import { GameCard } from '@/components/common/GameCard';
import { Logo } from '@/components/common/Logo';
import { SEO } from '@/components/common/SEO';
import { NearbyMatchesSection } from './home/NearbyMatchesSection';
import { LatestNewsSection } from './home/LatestNewsSection';
import { LiveUpcomingMatchesSection } from './home/LiveUpcomingMatchesSection';

export const HomePage = ({ sports }) => {
  const navigate = useNavigate();
  const [featuredGames, setFeaturedGames] = useState([]);
  const [liveGames, setLiveGames] = useState([]);
  const [upcomingGames, setUpcomingGames] = useState([]);
  const [replayGames, setReplayGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [liveScoreTexts, setLiveScoreTexts] = useState({});
  const [showInfoOverlay, setShowInfoOverlay] = useState(false);
  const [engagementScores, setEngagementScores] = useState({});

  useEffect(() => {
    const loadData = async () => {
      try {
        const [featured, live, upcoming, replays] = await Promise.all([
          api.getFeaturedGames(),
          api.getLiveGames(),
          api.getUpcomingGames(12),
          api.getReplayGames(12)
        ]);
        setFeaturedGames(featured.data);
        setLiveGames(live.data);
        setUpcomingGames(upcoming.data);
        setReplayGames(replays.data);

        // Load engagement scores
        try {
          const engRes = await api.getEngagementScores();
          setEngagementScores(engRes.data.scores || {});
        } catch {}
      } catch (error) {
        console.error('Error loading home data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Fetch live score text for games with score_feed_url
  useEffect(() => {
    const fetchLiveScores = async () => {
      const allGames = [...featuredGames, ...liveGames];
      const gamesWithFeed = allGames.filter(g => g.score_feed_url);
      
      for (const game of gamesWithFeed) {
        try {
          const res = await api.getGameLiveScore(game.id);
          if (res.data.text) {
            setLiveScoreTexts(prev => ({ ...prev, [game.id]: res.data.text }));
          }
        } catch (error) {
          console.error('Error fetching live score:', error);
        }
      }
    };

    if (featuredGames.length > 0 || liveGames.length > 0) {
      fetchLiveScores();
      const interval = setInterval(fetchLiveScores, 30000);
      return () => clearInterval(interval);
    }
  }, [featuredGames, liveGames]);

  // Auto-rotate hero carousel
  useEffect(() => {
    if (featuredGames.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % featuredGames.length);
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [featuredGames.length]);

  const currentFeatured = featuredGames[currentSlide];

  const handleWatchNow = (game) => {
    navigate(`/watch/${game.id}`);
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO 
        title="Home"
        description="Watch live soccer, GAA, hurling, and rugby matches. Get live scores, schedules, and stream your favorite teams in the United States."
        url="/"
      />
      {/* Hero Carousel - Peacock Style */}
      <section className="relative h-[75vh] min-h-[550px] overflow-hidden">
        {featuredGames.length > 0 ? (
          <>
            {featuredGames.map((game, index) => (
              <div
                key={game.id}
                className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
              >
                {/* Background Image - Prioritize hero image over thumbnail */}
                {game.background_image_url ? (
                  <img 
                    src={resolveMediaUrl(game.background_image_url)} 
                    alt="" 
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{
                      transform: `scale(${(game.hero_zoom || 100) / 100})`,
                      transformOrigin: `${game.hero_position_x || 50}% ${game.hero_position_y || 50}%`,
                      objectPosition: `${game.hero_position_x || 50}% ${game.hero_position_y || 50}%`
                    }}
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#1a2d4a] to-[#0d1a2d]" />
                )}
                {/* Gradient overlays for readability */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/30" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F202D] via-transparent to-transparent" />
                {/* Decorative diagonal line like Peacock */}
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute top-0 bottom-0 left-[45%] w-[2px] bg-gradient-to-b from-transparent via-amber-500/40 to-transparent transform -skew-x-12" />
                </div>
              </div>
            ))}

            <div className="relative z-10 h-full flex items-center">
              <div className="max-w-7xl mx-auto px-6 w-full">
                <div className="max-w-xl">
                  {/* Team Logos Row */}
                  <div className="flex items-center gap-4 mb-6">
                    {/* Home Team Logo */}
                    <div className={`w-16 h-16 md:w-20 md:h-20 rounded-lg flex items-center justify-center p-2 ${
                      currentFeatured?.home_team_logo ? '' : 'bg-white/10 backdrop-blur-sm'
                    }`}>
                      {currentFeatured?.home_team_logo ? (
                        <img src={resolveMediaUrl(currentFeatured.home_team_logo)} alt="" className="w-full h-full object-contain" />
                      ) : (
                        <span className="text-2xl md:text-3xl font-bold text-white">{currentFeatured?.home_team_name?.charAt(0) || 'H'}</span>
                      )}
                    </div>
                    {/* League Logo */}
                    <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center ${
                      currentFeatured?.league_logo ? 'p-1' : 'bg-white/10 backdrop-blur-sm'
                    }`}>
                      {currentFeatured?.league_logo ? (
                        <img src={resolveMediaUrl(currentFeatured.league_logo)} alt="" className="w-full h-full object-contain" />
                      ) : (
                        <span className="text-xs md:text-sm font-bold text-sky-300">{currentFeatured?.league_name?.split(' ').map(w => w[0]).join('') || 'VS'}</span>
                      )}
                    </div>
                    {/* Away Team Logo */}
                    <div className={`w-16 h-16 md:w-20 md:h-20 rounded-lg flex items-center justify-center p-2 ${
                      currentFeatured?.away_team_logo ? '' : 'bg-white/10 backdrop-blur-sm'
                    }`}>
                      {currentFeatured?.away_team_logo ? (
                        <img src={resolveMediaUrl(currentFeatured.away_team_logo)} alt="" className="w-full h-full object-contain" />
                      ) : (
                        <span className="text-2xl md:text-3xl font-bold text-white">{currentFeatured?.away_team_name?.charAt(0) || 'A'}</span>
                      )}
                    </div>
                  </div>

                  {/* Status Badge & Meta */}
                  <div className="flex items-center gap-3 mb-4">
                    {currentFeatured?.status === 'live' ? (
                      <span className="inline-flex items-center gap-2 bg-red-500 text-white px-3 py-1 rounded text-sm font-semibold">
                        <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                        Live
                      </span>
                    ) : currentFeatured?.status === 'finished' ? (
                      <span className="inline-flex items-center gap-2 bg-gray-700 text-white px-3 py-1 rounded text-sm font-medium border border-gray-600">
                        Replay
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-3 py-1 rounded text-sm font-medium border border-amber-500/30">
                        Upcoming
                      </span>
                    )}
                    <span className="text-gray-400 text-sm">
                      {formatDate(currentFeatured?.scheduled_time)} • {currentFeatured?.sport_name}
                    </span>
                  </div>

                  {/* Match Title - Vertical on mobile, horizontal on desktop */}
                  <div className="mb-3">
                    {/* Mobile: Vertical layout */}
                    <div className="md:hidden">
                      <h1 className="text-3xl font-bold text-white leading-tight">
                        {currentFeatured?.home_team_name || 'TBD'}
                      </h1>
                      <span className="text-gray-500 text-2xl font-bold block my-1">vs.</span>
                      <h1 className="text-3xl font-bold text-white leading-tight">
                        {currentFeatured?.away_team_name || 'TBD'}
                      </h1>
                    </div>
                    {/* Desktop: Horizontal layout */}
                    <h1 className="hidden md:block text-5xl font-bold text-white leading-tight">
                      {currentFeatured?.home_team_name || 'TBD'} <span className="text-gray-500">vs</span> {currentFeatured?.away_team_name || 'TBD'}
                    </h1>
                  </div>

                  {/* Score with team abbreviations (if live or finished) */}
                  {(currentFeatured?.status === 'live' || currentFeatured?.status === 'finished') && 
                   currentFeatured?.home_score !== null && (
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-white text-lg font-semibold hidden md:inline">
                        {currentFeatured?.home_team_name?.split(' ').slice(-1)[0]?.substring(0, 3).toUpperCase() || 'HOM'}
                      </span>
                      <span className="text-4xl md:text-5xl font-bold text-sky-400">
                        {currentFeatured.home_score} - {currentFeatured.away_score}
                      </span>
                      <span className="text-white text-lg font-semibold hidden md:inline">
                        {currentFeatured?.away_team_name?.split(' ').slice(-1)[0]?.substring(0, 3).toUpperCase() || 'AWY'}
                      </span>
                    </div>
                  )}

                  {/* Live Score Feed Text with team abbreviations */}
                  {liveScoreTexts[currentFeatured?.id] && (
                    <div className="flex items-center gap-2 text-sky-300 text-lg mb-4">
                      <span className="text-white font-semibold text-sm bg-white/10 px-2 py-0.5 rounded">
                        {currentFeatured?.home_team_name?.split(' ').slice(-1)[0]?.substring(0, 3).toUpperCase() || 'HOM'}
                      </span>
                      <span>{liveScoreTexts[currentFeatured.id]}</span>
                      <span className="text-white font-semibold text-sm bg-white/10 px-2 py-0.5 rounded">
                        {currentFeatured?.away_team_name?.split(' ').slice(-1)[0]?.substring(0, 3).toUpperCase() || 'AWY'}
                      </span>
                    </div>
                  )}

                  {/* League Info */}
                  <p className="text-gray-400 mb-6">{currentFeatured?.league_name}</p>

                  {/* Action Buttons - Peacock Style */}
                  <div className="flex items-center gap-4">
                    <Button 
                      onClick={() => handleWatchNow(currentFeatured)}
                      className="bg-amber-500 hover:bg-amber-400 text-black text-base px-6 py-5 rounded-md font-semibold"
                      data-testid="hero-watch-btn"
                    >
                      <Play className="w-5 h-5 mr-2 fill-current" />
                      {currentFeatured?.status === 'finished' ? 'WATCH REPLAY' : 'WATCH NOW'}
                    </Button>
                    <Button 
                      variant="outline"
                      className="border-gray-600 text-white hover:bg-white/10 text-base px-6 py-5 rounded-md"
                      onClick={() => setShowInfoOverlay(true)}
                      data-testid="hero-info-btn"
                    >
                      <Info className="w-5 h-5 mr-2" />
                      MORE INFO
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Info Overlay - Cable TV Style */}
            {showInfoOverlay && currentFeatured && (
              <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/70 backdrop-blur-sm transition-all duration-300">
                <div className="relative max-w-2xl w-full mx-4 bg-gradient-to-br from-gray-900/95 via-gray-800/95 to-gray-900/95 rounded-xl border border-gray-700/50 shadow-2xl overflow-hidden">
                  {/* Close Button */}
                  <button
                    onClick={() => setShowInfoOverlay(false)}
                    className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all z-10"
                    data-testid="info-close-btn"
                  >
                    <X className="w-5 h-5" />
                  </button>

                  {/* Header with gradient accent */}
                  <div className="h-1 bg-gradient-to-r from-amber-500 via-sky-500 to-amber-500" />
                  
                  <div className="p-6">
                    {/* Match Info Header */}
                    <div className="flex items-center gap-4 mb-4">
                      {/* Team Logos */}
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center p-1">
                          {currentFeatured.home_team_logo ? (
                            <img src={resolveMediaUrl(currentFeatured.home_team_logo)} alt="" className="w-full h-full object-contain" />
                          ) : (
                            <span className="text-lg font-bold text-white">{currentFeatured.home_team_name?.charAt(0)}</span>
                          )}
                        </div>
                        <span className="text-gray-500 text-sm font-medium">VS</span>
                        <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center p-1">
                          {currentFeatured.away_team_logo ? (
                            <img src={resolveMediaUrl(currentFeatured.away_team_logo)} alt="" className="w-full h-full object-contain" />
                          ) : (
                            <span className="text-lg font-bold text-white">{currentFeatured.away_team_name?.charAt(0)}</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Status Badge */}
                      <div className="ml-auto">
                        {currentFeatured.status === 'live' ? (
                          <span className="inline-flex items-center gap-2 bg-red-500 text-white px-3 py-1 rounded text-sm font-semibold">
                            <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                            LIVE
                          </span>
                        ) : currentFeatured.status === 'finished' ? (
                          <span className="bg-gray-700 text-white px-3 py-1 rounded text-sm font-medium">REPLAY</span>
                        ) : (
                          <span className="bg-amber-500/20 text-amber-400 px-3 py-1 rounded text-sm font-medium border border-amber-500/30">UPCOMING</span>
                        )}
                      </div>
                    </div>

                    {/* Match Title */}
                    <h2 className="text-2xl font-bold text-white mb-2">
                      {currentFeatured.home_team_name} vs {currentFeatured.away_team_name}
                    </h2>
                    
                    {/* Meta Info */}
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400 mb-4">
                      <span>{currentFeatured.league_name}</span>
                      <span className="w-1 h-1 rounded-full bg-gray-600"></span>
                      <span>{currentFeatured.sport_name}</span>
                      <span className="w-1 h-1 rounded-full bg-gray-600"></span>
                      <span>{formatDate(currentFeatured.scheduled_time)}</span>
                    </div>

                    {/* Score (if available) */}
                    {(currentFeatured.status === 'live' || currentFeatured.status === 'finished') && 
                     currentFeatured.home_score !== null && (
                      <div className="flex items-center gap-4 p-3 bg-white/5 rounded-lg mb-4">
                        <span className="text-white font-medium">{currentFeatured.home_team_name?.split(' ').slice(-1)[0]}</span>
                        <span className="text-3xl font-bold text-sky-400">{currentFeatured.home_score} - {currentFeatured.away_score}</span>
                        <span className="text-white font-medium">{currentFeatured.away_team_name?.split(' ').slice(-1)[0]}</span>
                      </div>
                    )}

                    {/* Description / Write-up */}
                    <div className="border-t border-gray-700/50 pt-4">
                      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">About This Match</h3>
                      <p className="text-gray-300 leading-relaxed">
                        {currentFeatured.featured_description || 
                          `Watch ${currentFeatured.home_team_name} take on ${currentFeatured.away_team_name} in this ${currentFeatured.league_name} matchup. ${
                            currentFeatured.status === 'live' ? 'The match is currently live!' : 
                            currentFeatured.status === 'finished' ? 'Catch the full replay of this exciting match.' :
                            `Kickoff scheduled for ${formatDate(currentFeatured.scheduled_time)}.`
                          }`
                        }
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 mt-6">
                      <Button 
                        onClick={() => {
                          setShowInfoOverlay(false);
                          handleWatchNow(currentFeatured);
                        }}
                        className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                      >
                        <Play className="w-4 h-4 mr-2 fill-current" />
                        {currentFeatured.status === 'finished' ? 'WATCH REPLAY' : 'WATCH NOW'}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => setShowInfoOverlay(false)}
                        className="border-gray-600 text-white hover:bg-white/10"
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Carousel Dots - Peacock Style */}
            {featuredGames.length > 1 && (
              <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
                {featuredGames.map((game, index) => (
                  <button
                    key={game.id}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      index === currentSlide 
                        ? 'bg-amber-500 w-6' 
                        : 'bg-white/30 hover:bg-white/50'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            )}

            {featuredGames.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentSlide(prev => (prev - 1 + featuredGames.length) % featuredGames.length)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white/80 hover:text-white transition-all"
                >
                  <ChevronRight className="w-5 h-5 rotate-180" />
                </button>
                <button
                  onClick={() => setCurrentSlide(prev => (prev + 1) % featuredGames.length)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white/80 hover:text-white transition-all"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </>
        ) : (
          <div className="relative h-full bg-gradient-to-br from-[#0F202D] via-[#1a3a4d] to-[#0a1520]">
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F202D] via-transparent to-transparent" />
            <div className="relative z-10 h-full flex items-center justify-center text-center px-4">
              <div>
                <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
                  Local Passion, <span className="text-sky-300">Global Reach</span>
                </h1>
                <p className="text-xl text-gray-300 mb-8">
                  Stream over 100+ matches in soccer, rugby, baseball and GAA
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  {sports.map(sport => (
                    <Link 
                      key={sport.id}
                      to={`/sport/${sport.slug}`}
                      className="px-6 py-3 bg-sky-400/20 hover:bg-sky-400/40 border border-sky-400/50 rounded-full text-white font-medium transition-all"
                    >
                      {sport.icon} {sport.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Live Games Row */}
      {liveGames.length > 0 && (
        <section className="py-8 px-4 bg-gradient-to-b from-[#0F202D] to-transparent">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white flex items-center">
                <span className="live-dot mr-2"></span> Live Now
              </h2>
              <Link to="/scores" className="text-sky-300 hover:text-sky-200 flex items-center text-sm">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
              {liveGames.map(game => (
                <div 
                  key={game.id}
                  onClick={() => handleWatchNow(game)}
                  className="flex-shrink-0 w-72 bg-gray-800/60 rounded-xl p-4 cursor-pointer hover:bg-gray-800/80 transition-colors border border-gray-700/50"
                >
                  <div className="flex items-center justify-between mb-3">
                    <Badge className="bg-red-500 text-white text-xs">LIVE</Badge>
                    <span className="text-gray-400 text-xs">{game.league_name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-white font-medium truncate">{game.home_team_name}</p>
                      <p className="text-gray-400 truncate">{game.away_team_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-bold text-xl">{game.home_score ?? 0}</p>
                      <p className="text-gray-400 font-bold text-xl">{game.away_score ?? 0}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Matches Row */}
      {featuredGames.length > 0 && (
        <section className="py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl font-bold text-white mb-4">Featured Matches</h2>
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
              {featuredGames.map(game => (
                <div 
                  key={game.id}
                  onClick={() => handleWatchNow(game)}
                  className="flex-shrink-0 w-80 relative rounded-xl overflow-hidden cursor-pointer group"
                >
                  {game.thumbnail ? (
                    <img src={resolveMediaUrl(game.thumbnail)} alt="" className="w-full h-48 object-cover" />
                  ) : (
                    <div className="w-full h-48 bg-gradient-to-br from-gray-700 to-gray-900" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      {game.status === 'live' && (
                        <Badge className="bg-red-500 text-white text-xs">LIVE</Badge>
                      )}
                      {game.status === 'finished' && (
                        <Badge className="bg-gray-600 text-white text-xs">REPLAY</Badge>
                      )}
                      <span className="text-gray-300 text-xs">{game.league_name}</span>
                    </div>
                    <p className="text-white font-semibold">
                      {game.home_team_name} vs {game.away_team_name}
                    </p>
                    {(game.status === 'live' || game.status === 'finished') && game.home_score !== null && (
                      <p className="text-sky-300 font-bold">{game.home_score} - {game.away_score}</p>
                    )}
                  </div>
                  <div className="absolute inset-0 bg-sky-500/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Play className="w-12 h-12 text-white" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Live & Upcoming Matches */}
      {/* Moved below Replays per user request */}

      {/* Upcoming Livestreams Section */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-white">Upcoming Livestreams</h2>
              <Badge className="bg-sky-500 text-white">Coming Soon</Badge>
            </div>
            {upcomingGames.length > 0 && (
              <Link to="/games/upcoming" className="text-sky-400 hover:text-sky-300 text-sm flex items-center gap-1" data-testid="view-all-upcoming">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            )}
          </div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="game-card animate-pulse">
                  <div className="aspect-video bg-gray-700 rounded-t-xl" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-gray-700 rounded w-1/2" />
                    <div className="h-5 bg-gray-700 rounded w-3/4" />
                    <div className="h-4 bg-gray-700 rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {upcomingGames.slice(0, 12).map(game => (
                <GameCard key={game.id} game={game} onWatch={() => navigate(`/watch/${game.id}`)} engagementScore={engagementScores[game.id]?.score || 0} />
              ))}
              {upcomingGames.length === 0 && (
                <div className="col-span-full text-center py-8">
                  <p className="text-gray-400">No upcoming livestreams scheduled.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Replays Section */}
      <section className="py-8 px-4 bg-gray-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-white">Replays</h2>
              <Badge className="bg-gray-600 text-white">Watch Again</Badge>
            </div>
            {replayGames.length > 0 && (
              <Link to="/games/replays" className="text-sky-400 hover:text-sky-300 text-sm flex items-center gap-1" data-testid="view-all-replays">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            )}
          </div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="game-card animate-pulse">
                  <div className="aspect-video bg-gray-700 rounded-t-xl" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-gray-700 rounded w-1/2" />
                    <div className="h-5 bg-gray-700 rounded w-3/4" />
                    <div className="h-4 bg-gray-700 rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {replayGames.slice(0, 12).map(game => (
                <GameCard key={game.id} game={game} onWatch={() => navigate(`/watch/${game.id}`)} engagementScore={engagementScores[game.id]?.score || 0} />
              ))}
              {replayGames.length === 0 && (
                <div className="col-span-full text-center py-8">
                  <p className="text-gray-400">No replays available yet.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Live & Upcoming Matches - positioned below Replays */}
      <LiveUpcomingMatchesSection onWatch={(game) => navigate(`/watch/${game.id}`)} />

      {/* Tagline Section */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h3 
            className="text-3xl md:text-4xl font-extrabold text-white mb-3"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Local Passion. <span className="text-sky-400">Global Reach.</span>
          </h3>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            The highest quality live streams for pre-professional sports in America.
          </p>
        </div>
      </section>

      {/* Subscribe Section */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* YouTube Subscribe */}
            <a 
              href="https://youtube.com/@newlogical?sub_confirmation=1" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-red-600 to-red-700 p-8 text-center hover:scale-[1.02] transition-transform"
              data-testid="subscribe-youtube"
            >
              <div className="flex flex-col items-center">
                <svg className="w-20 h-20 text-white mb-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                <h3 className="text-2xl font-bold text-white mb-2">Subscribe on YouTube</h3>
                <p className="text-red-100">Free highlights, replays & live streams</p>
              </div>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>

            {/* NLS+ Subscribe */}
            <a 
              href="https://plus.newlogicalsports.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group relative overflow-hidden rounded-2xl p-8 text-center hover:scale-[1.02] transition-transform border border-sky-900/50"
              style={{ backgroundColor: '#1a3650' }}
              data-testid="subscribe-nls-plus"
            >
              <div className="flex flex-col items-center">
                <Logo variant="plus" className="h-32 w-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">Subscribe to NLS+</h3>
                <p className="text-gray-300">Access to 100+ more premium matches</p>
              </div>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>
        </div>
      </section>

      {/* Local Passion. Global Reach Section with Nearby Matches */}
      <NearbyMatchesSection />

      {/* Social Media Section */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl font-bold text-white mb-2 text-center">Follow Us on Social Media</h2>
          <p className="text-gray-400 text-center mb-6">Stay updated with the latest matches, highlights & behind-the-scenes</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Instagram */}
            <a 
              href="https://instagram.com/newlogicalsports" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group relative overflow-hidden rounded-xl p-6 text-center hover:scale-[1.02] transition-transform"
              style={{ background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)' }}
              data-testid="social-instagram"
            >
              <svg className="w-10 h-10 text-white mx-auto mb-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              <p className="text-white font-semibold">Instagram</p>
              <p className="text-white/80 text-sm">@newlogicalsports</p>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>

            {/* TikTok */}
            <a 
              href="https://tiktok.com/@newlogical" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group relative overflow-hidden rounded-xl bg-black p-6 text-center hover:scale-[1.02] transition-transform border border-gray-800"
              data-testid="social-tiktok"
            >
              <svg className="w-10 h-10 text-white mx-auto mb-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
              <p className="text-white font-semibold">TikTok</p>
              <p className="text-gray-400 text-sm">@newlogical</p>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>

            {/* Facebook */}
            <a 
              href="https://www.facebook.com/newlogicalsports" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group relative overflow-hidden rounded-xl bg-[#1877f2] p-6 text-center hover:scale-[1.02] transition-transform"
              data-testid="social-facebook"
            >
              <svg className="w-10 h-10 text-white mx-auto mb-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <p className="text-white font-semibold">Facebook</p>
              <p className="text-white/80 text-sm">New Logical Sports</p>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>

            {/* X / Twitter */}
            <a 
              href="https://x.com/newlogicaltech" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group relative overflow-hidden rounded-xl bg-black p-6 text-center hover:scale-[1.02] transition-transform border border-gray-800"
              data-testid="social-twitter"
            >
              <svg className="w-10 h-10 text-white mx-auto mb-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              <p className="text-white font-semibold">X / Twitter</p>
              <p className="text-gray-400 text-sm">@newlogicaltech</p>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>
        </div>
      </section>

      {/* Latest News Section */}
      <LatestNewsSection />

      {/* Browse by Sport */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl font-bold text-white mb-4">Browse by Sport</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Sport thumbnails mapping */}
            {[
              { slug: 'soccer', name: 'Soccer', image: 'https://customer-assets.emergentagent.com/job_gamesfeed/artifacts/h95ygy8m_image.png' },
              { slug: 'gaelic-football', name: 'Gaelic Football', image: 'https://customer-assets.emergentagent.com/job_gamesfeed/artifacts/31r17hx7_image.png' },
              { slug: 'hurling-camogie', name: 'Hurling / Camogie', image: 'https://customer-assets.emergentagent.com/job_gamesfeed/artifacts/uytwlbk6_image.png' },
              { slug: 'rugby', name: 'Rugby', image: 'https://customer-assets.emergentagent.com/job_gamesfeed/artifacts/y82usxwu_image.png' },
            ].map(sportItem => (
              <Link 
                key={sportItem.slug}
                to={`/sport/${sportItem.slug}`}
                className="group relative rounded-xl overflow-hidden hover:scale-[1.02] transition-transform"
                data-testid={`browse-sport-${sportItem.slug}`}
              >
                <div className="aspect-[4/3] relative">
                  <img 
                    src={sportItem.image} 
                    alt={sportItem.name} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  <div className="absolute inset-0 bg-sky-500/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
                    <h3 className="text-xl font-bold text-white mb-1">{sportItem.name}</h3>
                    <span className="text-sky-300 text-sm flex items-center justify-center gap-1">
                      Browse Leagues <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;
